import React from 'react';
import { getIntl } from '../../../../utils/intl';
import { FormStack } from '../../../v2BaseComponents/Stack';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import { Field } from 'formik';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { email, getDateFormatValidator, phoneNumber, required } from '../../../../utils/validators';
import { APITypes } from '../../../../primary-record-types';

const intl = getIntl('ClientRecordFields', {
    clientFirstName: 'Client First Name',
    clientLastName: 'Client Last Name',
    clientDOB: 'Client Date of Birth',
    clientPreferredName: 'Client Preferred Name',
    pointOfContactFirstName: 'Point of Contact First Name',
    pointOfContactLastName: 'Point of Contact Last Name',
    pointOfContactEmail: 'Point of Contact Email',
    pointOfContactPreferredName: 'Point of Contact Preferred Name',
    pointOfContactPhoneNumber: 'Point of Contact Phone Number',
});

export type ClientRecordFieldsOwnProps = {};

export type ClientRecordFieldsProps = ClientRecordFieldsOwnProps;

export type FormValue = {
    clientFirstName: string;
    clientLastName: string;
    clientDOB: string;
    clientPreferredName: string;
    pointOfContactFirstName: string;
    pointOfContactLastName: string;
    pointOfContactEmail: string;
    pointOfContactPreferredName: string;
    pointOfContactPhoneNumber: string;
};

export const initialFormValue: FormValue = {
    clientFirstName: '',
    clientLastName: '',
    clientDOB: '',
    clientPreferredName: '',
    pointOfContactFirstName: '',
    pointOfContactLastName: '',
    pointOfContactEmail: '',
    pointOfContactPreferredName: '',
    pointOfContactPhoneNumber: '',
};

export const formValuesFromClientRecord = (
    clientRecord: APITypes.organizationService.OrganizationClientRecordDetails,
): FormValue => {
    return {
        clientFirstName: clientRecord.clientDetails.firstName || '',
        clientLastName: clientRecord.clientDetails.lastName || '',
        clientDOB: clientRecord.clientDetails.dob || '',
        clientPreferredName: clientRecord.clientDetails.preferredName || '',
        pointOfContactFirstName: clientRecord.clientOrganizerDetails.firstName || '',
        pointOfContactLastName: clientRecord.clientOrganizerDetails.lastName || '',
        pointOfContactEmail: clientRecord.clientOrganizerDetails.email || '',
        pointOfContactPreferredName: clientRecord.clientOrganizerDetails.preferredName || '',
        pointOfContactPhoneNumber: clientRecord.clientOrganizerDetails.phoneNumber || '',
    };
};

export const formValueToCreateRequest = (
    formValue: FormValue,
): APITypes.organizationService.CreateOrganizationClientRecordRequest => {
    const base: APITypes.organizationService.CreateOrganizationClientRecordRequest = {
        clientDetails: {},
        clientOrganizerDetails: {},
    };

    if (formValue.clientFirstName) {
        base.clientDetails.firstName = formValue.clientFirstName;
    }

    if (formValue.clientLastName) {
        base.clientDetails.lastName = formValue.clientLastName;
    }

    if (formValue.clientDOB) {
        base.clientDetails.dob = formValue.clientDOB;
    }

    if (formValue.clientPreferredName) {
        base.clientDetails.preferredName = formValue.clientPreferredName;
    }

    if (formValue.pointOfContactFirstName) {
        base.clientOrganizerDetails.firstName = formValue.pointOfContactFirstName;
    }

    if (formValue.pointOfContactLastName) {
        base.clientOrganizerDetails.lastName = formValue.pointOfContactLastName;
    }

    if (formValue.pointOfContactEmail) {
        base.clientOrganizerDetails.email = formValue.pointOfContactEmail;
    }

    if (formValue.pointOfContactPreferredName) {
        base.clientOrganizerDetails.preferredName = formValue.pointOfContactPreferredName;
    }

    if (formValue.pointOfContactPhoneNumber) {
        base.clientOrganizerDetails.phoneNumber = formValue.pointOfContactPhoneNumber;
    }

    return base;
};

const ClientRecordFields: React.FC<ClientRecordFieldsProps> = (_props) => {
    return (
        <FormStack>
            <SplitFormRow>
                <Field
                    name="clientFirstName"
                    component={FormikInputs.Input}
                    title={intl('clientFirstName')}
                    validate={required}
                    isRequired
                />
                <Field
                    name="clientLastName"
                    component={FormikInputs.Input}
                    title={intl('clientLastName')}
                    validate={required}
                    isRequired
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field name="clientPreferredName" component={FormikInputs.Input} title={intl('clientPreferredName')} />
                <Field
                    name="clientDOB"
                    component={FormikInputs.DateTimePicker}
                    title={intl('clientDOB')}
                    validate={getDateFormatValidator('MM/DD/YYYY')}
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="pointOfContactFirstName"
                    component={FormikInputs.Input}
                    title={intl('pointOfContactFirstName')}
                    validate={required}
                    isRequired
                />
                <Field
                    name="pointOfContactLastName"
                    component={FormikInputs.Input}
                    title={intl('pointOfContactLastName')}
                    validate={required}
                    isRequired
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="pointOfContactPreferredName"
                    component={FormikInputs.Input}
                    title={intl('pointOfContactPreferredName')}
                />
            </SplitFormRow>
            <SplitFormRow>
                <Field
                    name="pointOfContactEmail"
                    component={FormikInputs.Input}
                    title={intl('pointOfContactEmail')}
                    validate={email}
                />
                <Field
                    name="pointOfContactPhoneNumber"
                    component={FormikInputs.PhoneNumber}
                    title={intl('pointOfContactPhoneNumber')}
                    validate={phoneNumber}
                />
            </SplitFormRow>
        </FormStack>
    );
};

export default React.memo(ClientRecordFields);
